import { Constants } from "global/Application/Constants";
import { AppDataSource } from "global/DataSources/AppDataSource";
import { parseDate } from "global/Utility/TSFunctions";
import {
    bcbs_config_release as IXrmTypes_bcbs_config_release,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot
} from 'model/CRM';
import * as IXrmTypesExtended from 'model/CRM.type';
import { IGroupConfigurationConfig, IGroupConfigurationData } from "model/FlexibleAttributeModel";
import {
    IGroupConfigurationData as FAM_IGroupConfigurationData
} from '../../model/FlexibleAttributeModel';
import { ApplyGroupConfigurationSnapshotTransformer } from "./ApplyGroupConfigurationSnapshotTransformer";
import { ClearEffectiveDateTransformer } from "./ClearEffectiveDateTransformer";
import { CloneTransformer } from "./CloneTransformer";
import { DeprecateFieldsTransformer } from "./DeprecateFieldsTransformer";
import { EnsurePropertiesAreInitializedTransformer } from "./EnsurePropertiesAreInitializedTransformer";
import { ETransformContextType, IJsonTransformer } from "./IJsonTransformer";
import { PharmacyResetTransformer } from "./PharmacyResetTransformer";
import { RemoveCancelledDataTransformer } from "./RemoveCancelledDataTransformer";
import { RemoveCommentsTransformer } from "./RemoveCommentsTransformer";
import { RemoveDeactivatedDataTransformer } from "./RemoveDeactivatedDataTransformer";
import { IReplaceIdsScope, ReplaceIdsTransformer } from "./ReplaceIdsTransformer";
import { RevisionResetTransformer } from "./RevisionResetTransformer";
import { Sprint17Transformer } from "./Sprint17Transformer";
import { Sprint18Transformer } from "./Sprint18Transformer";
import { Sprint21Transformer } from "./Sprint21Transformer";
import { Sprint22Transformer } from "./Sprint22Transformer";
import { Sprint24Transformer } from "./Sprint24Transformer";
import { Sprint33PatchTransformer } from "./Sprint33PatchTransformer";
import { Sprint33Transformer } from "./Sprint33Transformer";
import { Sprint34Transformer } from "./Sprint34Transformer";
import { Sprint35Transformer } from "./Sprint35Transformer";
import { Sprint40Transformer } from "./Sprint40Transformer";
import { Sprint43Transformer } from "./Sprint43Transformer";
import { UpdateAttributesTransformer } from "./UpdateAttributesTransformer";
import { UpdateBenefitCategoryPropertiesTransformer } from "./UpdateBenefitCategoryPropertiesTransformer";
import { UpdateFeesAndPremiumCoverageTransformer } from "./UpdateFeesAndPremiumCoverageTransformer";
import { UpdateIterationTransformer } from "./UpdateIterationTransformer";
import { UpdateLookupValueNameTransformer } from "./UpdateLookupValueNameTransformer";
import { UpdateOptionSetItemLogicalNameTransformer } from "./UpdateOptionSetItemLogicalNameTransformer";
import { VisibilityTransformer } from "./VisibilityTransformer";
import { RemoveNonstandardTemplateTransformer } from "./RemoveNonstandardTemplate";

export enum EJsonTransformerRuntime {
    /** Transformation is applied by context, each transformer runs for context, moving to next context */
    EDefault,

    /** Transformation is applied sequentially for each transformer, finishing all stages before starting next transformer */
    ESequential
}

export class JsonTransformer {
    readonly transformers: IJsonTransformer[];
    readonly nextJsonTransformer?: JsonTransformer;
    readonly runType: EJsonTransformerRuntime;

    /**
     * 
     * @param transformers list of transformers to run
     * @param runType
     * @param nextJsonTransformer 
     * *in sequential mode, individual nextJsonTransformer is ignored
     */
    constructor(transformers: IJsonTransformer[], runType?: EJsonTransformerRuntime, nextJsonTransformer?: JsonTransformer) {
        console.log("[tran] JsonTransformer.constructor", transformers)

        this.transformers = transformers;

        this.nextJsonTransformer = nextJsonTransformer;

        if (nextJsonTransformer === this)
            throw new Error("Next transformer can not be the same as the running transformer!");

        this.runType = runType ?? EJsonTransformerRuntime.EDefault;
    }

    async transformAsync(json: IGroupConfigurationData, snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot): Promise<void> {

        if (this.runType === EJsonTransformerRuntime.EDefault) {
            // if default run is requested
            await this._transformAsync(json, snapshot);
        }
        else if (this.runType === EJsonTransformerRuntime.ESequential) {

            // if sequential run is requested, lets bulk generate JsonTransformers and await run on each
            const jsonTransformers = this.transformers.map(q => {
                return new JsonTransformer([q], EJsonTransformerRuntime.EDefault, undefined);
            });

            for (const jt of jsonTransformers)
                await jt.transformAsync(json, snapshot);

        } else {
            throw new Error("Unsupported run time!");
        }

        if (this.nextJsonTransformer)
            await this.nextJsonTransformer.transformAsync(json, snapshot);
    }

    private async _transformAsync(json: IGroupConfigurationData, snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot): Promise<void> {
        // NOTE: the order of processing the arrays of the JSON 
        // are intentional - we want to process the items with no
        // Refs to other items first, then slowly end with those
        // items that have Refs.  This provides for some performance
        // benefits in some of the transformers

        for (const transform of this.transformers) {
            if (transform.before)
                await transform.before(json);
        }

        // root level transforms
        for (const transform of this.transformers) {
            if (transform.transformGroupConfiguration)
                await transform.transformGroupConfiguration(json, snapshot);
        }

        // root level attribute set transforms
        await this.process(
            json.attributeSets,
            (obj, transform) => {
                return transform.transformAttributeSet(obj, {
                    type: ETransformContextType.GroupConfiguration,
                    parent: json
                });
            }
        );

        // root level non-standards
        await this.process(
            json.nonStandards,
            (obj, transform) => {
                return transform.transformNonStandard(obj, {
                    type: ETransformContextType.GroupConfiguration,
                    parent: json
                });
            }
        );

        // root level non-standards
        await this.process(
            json.nonStandardTemplates,
            (nonStandardTemplate, transform) => {
                return transform.transformNonStandardTemplate(nonStandardTemplate)
            }
        );

        // benefits
        await this.process(
            json.benefits,
            async (benefit, transform) => {
                return transform.transformBenefit(benefit);
            },
            async (benefit) => {

                await this.process(benefit.attributeSets, (attributeSet, transform) => {
                    return transform.transformAttributeSet(attributeSet, {
                        type: ETransformContextType.Benefit,
                        parent: benefit
                    });
                });

                await this.process(benefit.nonStandards, (nonStandard, transform) => {
                    return transform.transformNonStandard(nonStandard, {
                        type: ETransformContextType.Benefit,
                        parent: benefit
                    });
                });

            }
        );

        // accounts receivables
        await this.process(
            json.claimsARs,
            async (ar, transform) => {
                return transform.transformAccountsReceivable(ar);
            },
            async (accountsReceivable) => {

                await this.process(accountsReceivable.attributeSets, (attributeSet, transform) => {
                    return transform.transformAttributeSet(attributeSet, {
                        type: ETransformContextType.AccountsReceivable,
                        parent: accountsReceivable
                    });
                });

                await this.process(accountsReceivable.nonStandards, (nonStandard, transform) => {
                    return transform.transformNonStandard(nonStandard, {
                        type: ETransformContextType.AccountsReceivable,
                        parent: accountsReceivable
                    });
                });
            }
        );

        // stop loss
        await this.process(
            json.stopLoss,
            async (stopLoss, transform) => {
                return transform.transformStopLoss(stopLoss);
            },
            async (stopLoss) => {

                await this.process(stopLoss.attributeSets, (attributeSet, transform) => {
                    return transform.transformAttributeSet(attributeSet, {
                        type: ETransformContextType.StopLoss,
                        parent: stopLoss
                    });
                });

                await this.process(stopLoss.nonStandards, (nonStandard, transform) => {
                    return transform.transformNonStandard(nonStandard, {
                        type: ETransformContextType.StopLoss,
                        parent: stopLoss
                    });
                });
            }
        );

        // fees and premiums
        await this.process(
            json.feesAndPremiums,
            async (feesAndPremium, transform) => {
                return transform.transformFeesAndPremium(feesAndPremium);
            },
            async (feesAndPremium) => {

                await this.process(feesAndPremium.attributeSets, (attributeSet, transform) => {
                    return transform.transformAttributeSet(attributeSet, {
                        type: ETransformContextType.FeesAndPremium,
                        parent: feesAndPremium
                    });
                });

                await this.process(feesAndPremium.nonStandards, (nonStandard, transform) => {
                    return transform.transformNonStandard(nonStandard, {
                        type: ETransformContextType.StopLoss,
                        parent: feesAndPremium
                    });
                });
            }
        );

        // plans
        await this.process(
            json.plans,
            async (plan, transform) => {
                return transform.transformPlan(plan);
            },
            async (plan) => {

                await this.process(plan.attributeSets, (attrset, transform) => {
                    return transform.transformAttributeSet(attrset, {
                        type: ETransformContextType.Plan,
                        parent: plan
                    });
                });

                await this.process(plan.nonStandards, (nonStandard, transform) => {
                    return transform.transformNonStandard(nonStandard, {
                        type: ETransformContextType.Plan,
                        parent: plan
                    });
                });
            });

        // group structure (CES Numbers)
        await this.process(json.groupStructure.cesNumbers, (ces, transform) => {
            return transform.transformCESNumber(ces);
        });

        // group structure (Departments)
        await this.process(json.groupStructure.departments, (dept, transform) => {
            return transform.transformDepartment(dept);
        });

        // group structure (Group Numbers)
        await this.process(json.groupStructure.groupNumbers, (gn, transform) => {
            return transform.transformGroupNumber(gn);
        });


        for (const transform of this.transformers) {
            await transform.after(json);
        }
    }

    private async process<T>(
        array: T[],
        onTransform: (obj: T, transform: IJsonTransformer) => Promise<boolean | void>,
        onComplete?: (obj: T) => Promise<void>): Promise<void> {

        if (!array || !array.length)
            return;

        let n = array.length;
        for (let i = n - 1; i >= 0; i--) {
            let obj = array[i];

            if (!obj) {
                array.splice(i, 1);
                continue;
            }

            let remove = false;
            for (const transform of this.transformers) {
                let temp = await onTransform(obj, transform);
                if (temp) {
                    // once a transformer marks it as removed
                    // then don't process subsequent transforms
                    remove = true;
                    break;
                }
            }

            if (remove) {
                // a transform marked it as remove, so remove it
                array.splice(i, 1);
                // don't bother calling the onComplete since we removed it
                continue;
            }

            if (onComplete)
                await onComplete(obj);
        }
    }


    private static getTransformerForReadonly(config: IGroupConfigurationConfig, dataSource: AppDataSource, snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot): JsonTransformer {
        return new JsonTransformer([
            new EnsurePropertiesAreInitializedTransformer(),
            new DeprecateFieldsTransformer(),
            new VisibilityTransformer(config, dataSource),
        ]);
    }

    public static getTransformerForEdit(
        config: IGroupConfigurationConfig,
        dataSource: AppDataSource,
        bcbs_groupconfigurationsnapshot: IXrmTypes_bcbs_groupconfigurationsnapshot,
        account: IXrmTypesExtended.IAccountExtended,
        bcbs_config_release: IXrmTypes_bcbs_config_release,
        json: FAM_IGroupConfigurationData
    ): JsonTransformer {

        console.log("[tran] JsonTransformers.getTransformerForEdit", {
            jsonIteration: json.iteration,
            jsonReleaseVersion: json.releaseVersion,
            bcbs_config_release: {
                bcbs_versionnumber: bcbs_config_release.bcbs_versionnumber,
                bcbs_name: bcbs_config_release.bcbs_name,
            }
        });

        const defaultTransformer = new JsonTransformer([
            new EnsurePropertiesAreInitializedTransformer(),
            new ApplyGroupConfigurationSnapshotTransformer(config, account),
            new DeprecateFieldsTransformer(),
            new UpdateBenefitCategoryPropertiesTransformer(config),
            new UpdateAttributesTransformer(config),
            new UpdateFeesAndPremiumCoverageTransformer(config),
            new UpdateOptionSetItemLogicalNameTransformer(),
            new UpdateLookupValueNameTransformer(dataSource),
            // new FeeNameTransformer(),
            new VisibilityTransformer(config, dataSource),
            // new UpdateIterationTransformer(config),
        ]);

        const currentReleaseVersionStr = (bcbs_config_release.bcbs_versionnumber ?? '').match(/\d+/i)?.[0] ?? '0';
        const currentReleaseCreatedOn = new Date(bcbs_config_release.createdon!).getTime();
        const currentReleaseVersion = parseInt(currentReleaseVersionStr);

        const jsonReleaseVersionStr = (json.releaseVersion ?? '').match(/\d+/i)?.[0] ?? '0';
        const jsonReleaseVersion = parseInt(jsonReleaseVersionStr);
        const bcbs_groupconfigurationsnapshotCreatedOn = new Date(bcbs_groupconfigurationsnapshot.createdon!).getTime();

        // Until we figure out more permanent solution
        // Please refer to PBI

        // TODO move all these to constants file
        const sprint17ReleaseDate = new Date('2023-07-05 03:29:26.000Z').getTime();
        const sprint18ReleaseDate = new Date('2023-07-05 03:29:26.000Z').getTime();
        // https://bcbsonline.crm.dynamics.com/main.aspx?etn=bcbs_config_release&pagetype=entityrecord&id=aa95fa66-186a-ee11-9ae7-000d3a99809d
        const sprint21ReleaseDate = new Date('2023-10-13 22:32:44.000Z').getTime();
        const sprint22ReleaseDate = new Date('2023-11-07 15:36:00.000Z').getTime();
        const sprint24ReleaseDate = new Date('2024-01-03 03:56:50.535Z').getTime();
        const sprint30ReleaseDate = new Date('2024-06-28T15:14:22.102Z').getTime();
        const sprint33ReleaseDate = new Date('2024-09-28T15:14:22.102Z').getTime();
        const sprint33PatchReleaseDate = new Date('2024-10-02T18:51:46.159Z').getTime();
        const sprint34ReleaseDate = new Date('2024-11-08T15:14:22.102Z').getTime();
        const sprint35ReleaseDate = new Date('2025-04-04T15:14:22.102Z').getTime();


        // Migration Transformers
        // -----------------------------------------------------
        const migrationTransformers: IJsonTransformer[] = [];

        // adding a default one, since we add all fields
        migrationTransformers.push(new UpdateAttributesTransformer(config))

        // **********************************************
        // Sprint17Transformer
        // Ancillary Benefit - Updates to "Shopping for Care" / Transparency
        // Medical Benefit - Dental / Drug / Vision Benefit Tab - SMBM Section - New Medical Specialty Rebate field and Rx Pre-Auth Migration
        // Medical Benefit - Therapy - Remove Separate PCP/Specialist Flag from PT/OT and ST Facility Attributes
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/367520
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/341845
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/399750
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1027 && 1027 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1027 && 1027 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint17) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint17ReleaseDate) {
                    migrationTransformers.push(new Sprint17Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint17-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint17-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint17-jsonversion")
        }

        // **********************************************
        // Sprint18Transformer
        // Fees and Premiums - General Tab - EFFECTIVE DATES - Consolidate Medical Fields
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/374576
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/412925
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1032 && 1032 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1032 && 1032 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint18) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint18ReleaseDate) {
                    migrationTransformers.push(new Sprint18Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint18-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint18-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint18-jsonversion")
        }

        // **********************************************
        // Sprint21Transformer
        // Shared Savings Transformer
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/377650
        // https://dev.azure.com/blue-infosys/Group%20Library/_workitems/edit/412925
        // TODO implement created on after>snapshotrelease date
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1050 && 1050 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1050 && 1050 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint21) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint21ReleaseDate) {
                    migrationTransformers.push(new Sprint21Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint21-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint21-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint21-jsonversion")
        }

        // **********************************************
        // Sprint22Transformer
        // Shared Savings Transformer
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1054 && 1054 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1054 && 1054 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint22) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint22ReleaseDate) {
                    migrationTransformers.push(new Sprint22Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint22-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint22-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint22-jsonversion")
        }

        // **********************************************
        // Sprint24Transformer
        // Retroactive Termination Claims Recoveries
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1061 && 1061 > jsonReleaseVersion)
            || (Constants.env === "Production" && currentReleaseVersion >= 1061 && 1061 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint24) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint24ReleaseDate) {
                    migrationTransformers.push(new Sprint24Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint24-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint24-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint24-jsonversion")
        }

        // **********************************************
        // Sprint30Transformer
        // Retroactive Termination Claims Recoveries
        // **********************************************
        // No-Longer needed, aba therapy was rolled back
        // if (
        //     (Constants.env === "System" && currentReleaseVersion >= 1120 && 1120 > jsonReleaseVersion)
        //     || (Constants.env === "Production" && currentReleaseVersion >= 1076 && 1076 > jsonReleaseVersion)
        // ) {
        //     if (!json.transformers?.Sprint30) {
        //         if (bcbs_groupconfigurationsnapshotCreatedOn > sprint30ReleaseDate) {
        //             migrationTransformers.push(new Sprint30Transformer({ config, json }))
        //         } else {
        //             console.debug('[tran] getTransformerForEdit', "skip-sprint30-createdon")
        //         }
        //     } else {
        //         console.debug('[tran] getTransformerForEdit', "skip-sprint30-marked")
        //     }
        // } else {
        //     console.debug('[tran] getTransformerForEdit', "skip-sprint30-jsonversion")
        // }

        // **********************************************
        // Sprint33Transformer
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1083 && 1083 > jsonReleaseVersion)
            || (Constants.env === "Production" && currentReleaseVersion >= 1083 && 1083 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint33) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint33ReleaseDate) {
                    migrationTransformers.push(new Sprint33Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint33-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint33-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint33-jsonversion")
        }


        // **********************************************
        // Sprint33PatchTransformer
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1088 && 1088 > jsonReleaseVersion)
            || (Constants.env === "Production" && currentReleaseVersion >= 1088 && 1088 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint33PatchTransformer) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint33PatchReleaseDate) {
                    migrationTransformers.push(new Sprint33PatchTransformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint33patchtransformer-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint33patchtransformer-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint33patchtransformer-jsonversion")
        }

        /*
        // **********************************************
        // Sprint34Transformer
        // **********************************************
        */
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1093 && 1093 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1093 && 1093 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint34) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint34ReleaseDate) {
                    migrationTransformers.push(new Sprint34Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint34-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint34-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint34-jsonversion")
        }


        // **********************************************
        // Sprint35Transformer
        // **********************************************
        if (
            (Constants.env === "System" && currentReleaseVersion >= 1116 && 1116 > jsonReleaseVersion) ||
            (Constants.env === "Production" && currentReleaseVersion >= 1107 && 1107 > jsonReleaseVersion)
        ) {
            if (!json.transformers?.Sprint35) {
                if (bcbs_groupconfigurationsnapshotCreatedOn > sprint35ReleaseDate) {
                    migrationTransformers.push(new Sprint35Transformer({ config, json }))
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint35transformer-createdon")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint35transformer-marked")
            }
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint35transformer-jsonversion")
        }


        // **********************************************
        // Sprint40Transformer
        // **********************************************

        const bdsDate = parseDate("2025-09-30", 'yyyy-MM-ddThh:mm:ssZ as local').getTime();
        const jsonEffectiveDate = json.effectiveDate ? parseDate(json.effectiveDate.value, 'yyyy-MM-ddThh:mm:ssZ as local').getTime() : 0;

        if (jsonEffectiveDate > bdsDate) {
            if (
                (
                    Constants.env === "System" &&
                    currentReleaseVersion >= Constants.sprintInfo.sprint40.systemVersion
                    // Constants.sprintInfo.sprint40.systemVersion > jsonReleaseVersion
                ) ||
                (
                    Constants.env === "Production" &&
                    currentReleaseVersion >= Constants.sprintInfo.sprint40.productionVersion
                    // Constants.sprintInfo.sprint40.productionVersion > jsonReleaseVersion
                )
            ) {

                if (!json.transformers?.Sprint40) {
                    if (bcbs_groupconfigurationsnapshotCreatedOn > Constants.sprintInfo.sprint40.releaseTime) {
                        migrationTransformers.push(new Sprint40Transformer({ config, json }))
                    } else {
                        console.debug('[tran] getTransformerForEdit', "skip-sprint40transformer-createdon")
                    }
                } else {
                    console.debug('[tran] getTransformerForEdit', "skip-sprint40transformer-marked")
                }
            } else {
                console.debug('[tran] getTransformerForEdit', "skip-sprint40transformer-jsonversion")
            }
        }

        // **********************************************
        // Sprint43Transformer
        // SafeGuard removed because this is fixing an issue in the model that should be applied universally from original implementation
        // **********************************************

        if (!json.transformers?.Sprint43) {
            migrationTransformers.push(new Sprint43Transformer({ config, json }))
        } else {
            console.debug('[tran] getTransformerForEdit', "skip-sprint43transformer-marked")
        }

        //migrationTransformers.push(new Sprint43Transformer({ config, json }))


        // any migrations to run? first migration and the followed by default transformer
        if (migrationTransformers.length > 1)
            return new JsonTransformer(
                migrationTransformers,
                EJsonTransformerRuntime.ESequential,
                defaultTransformer
            );

        return defaultTransformer;
    }

    // private static getTransformerForNew(
    //     config: IGroupConfigurationConfig,
    //     dataSource: AppDataSource,
    //     snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot
    // ): JsonTransformer {
    //     return new JsonTransformer([
    //         new EnsurePropertiesAreInitializedTransformer(),
    //         new DeprecateFieldsTransformer(),
    //         new ClearEffectiveDateTransformer(),
    //         new RemoveCancelledDataTransformer(),
    //         new RemoveDeactivatedDataTransformer(),
    //         new RemoveCommentsTransformer(),
    //         new UpdateBenefitCategoryPropertiesTransformer(config),
    //         new UpdateAttributesTransformer(config),
    //         new UpdateFeesAndPremiumCoverageTransformer(config),
    //         new UpdateOptionSetItemLogicalNameTransformer(),
    //         new UpdateLookupValueNameTransformer(dataSource),
    //         new UpdateIterationTransformer(),
    //         new VisibilityTransformer(config, dataSource),
    //     ]);
    // }

    static getTransformerForCopy(
        config: IGroupConfigurationConfig,
        dataSource: AppDataSource,
        snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot,
        scope: IReplaceIdsScope
    ): JsonTransformer {

        console.log("[tran] JsonTransformers.getTransformerForCopy");

        // TODO figure out copy transformer id issue
        // there was a bug that was caused by the order(I think), postponing until later with more time to understand the issue.
        const stage2 = new JsonTransformer([
            new ReplaceIdsTransformer(scope)
        ]);

        const stage1 = new JsonTransformer(
            [
                new EnsurePropertiesAreInitializedTransformer(),
                new DeprecateFieldsTransformer(),
                new ClearEffectiveDateTransformer(),
                new RemoveCancelledDataTransformer(),
                new RemoveDeactivatedDataTransformer(),
                new RemoveCommentsTransformer(),
                new UpdateBenefitCategoryPropertiesTransformer(config),
                new UpdateAttributesTransformer(config),
                new UpdateOptionSetItemLogicalNameTransformer(),
                new UpdateLookupValueNameTransformer(dataSource),
                new UpdateIterationTransformer(config),
                new CloneTransformer(config, snapshot, false, null),
                new PharmacyResetTransformer(),
                new RevisionResetTransformer(),
                new RemoveNonstandardTemplateTransformer(),
                new VisibilityTransformer(config, dataSource),
            ],
            EJsonTransformerRuntime.EDefault,
            stage2
        );

        return stage1;
    }

    static getTransformerForCopyInternal(
        config: IGroupConfigurationConfig,
        dataSource: AppDataSource,
        snapshot: IXrmTypes_bcbs_groupconfigurationsnapshot,
        scope: IReplaceIdsScope
    ): JsonTransformer {
        return JsonTransformer.getTransformerForCopy(config, dataSource, snapshot, scope);
    }

}
